/**
 * The Op — the ONLY mutation unit anywhere (ARCHITECTURE.md §4.1, CONSTRAINTS #1).
 * Autosave, offline replay, Sheet reconcile, chat apply, _INBOX triage, and undo
 * ALL emit this and nothing else.
 */
import { z } from "zod";
import { ulid } from "ulid";
import { isHLC, type HLC } from "./hlc.ts";

/**
 * NOTE: the canonical CollectionKey union lives in `packages/registry` — collection
 * names exist ONLY there (CONSTRAINTS #5), so sync-core is deliberately
 * collection-agnostic and types the key as `string`. Consumers that import the
 * registry get the narrowed union at their own boundary; sync-core never inspects
 * the collection name.
 */
export type CollectionKey = string;

export const OP_KINDS = [
  "put_fields",
  "create_item",
  "delete",
  "restore",
  "create_gig",
  "delete_gig",
] as const;
export type OpKind = (typeof OP_KINDS)[number];

export const OP_ORIGINS = ["app", "sheet", "chat", "triage", "undo"] as const;
export type OpOrigin = (typeof OP_ORIGINS)[number];

/** Current op-format version (the `v` field). */
export const OP_FORMAT_VERSION = 1;

export interface OpTarget {
  collection: CollectionKey;
  item_id: string;
}

export interface Op {
  /** Op-format version. */
  v: number;
  /** ULID — idempotency key, unique forever. */
  op_id: string;
  /** Groups one user gesture / one apply-card. */
  txn_id: string;
  /** User id or system principal. */
  actor: string;
  origin: OpOrigin;
  gig_id: string;
  target: OpTarget;
  kind: OpKind;
  /** ONLY the touched fields — never item snapshots (verdict registry-evolve). */
  fields: Record<string, unknown>;
  /** Hybrid logical clock stamp (§4.2). */
  hlc: HLC;
  /** Registry version the op was authored under. */
  schema_version: number;
  /** Derived from registry dedupFields — content-level dedup. */
  dedup_key?: string;
}

/**
 * Wire validation for the Op envelope. Field-VALUE validation is the registry's
 * job (ARCHITECTURE §3: "validation happens here, nowhere else" — per field);
 * this schema only guards the op format itself.
 *
 * NOTE on op_id: real ids are ULIDs (26-char Crockford base32, see mintOpId), but
 * the schema deliberately accepts any 20–40 char [0-9A-Za-z] token: the P1 smoke
 * test (docs/phases/P1.md step 8) posts the literal op_id
 * "01TESTOPULID0000000000001", which is 25 chars and contains non-Crockford
 * letters. Idempotency only needs uniqueness, not the ULID alphabet.
 */
export const OpSchema = z
  .object({
    v: z.number().int().min(1),
    op_id: z.string().regex(/^[0-9A-Za-z]{20,40}$/, "op_id must be a 20-40 char id (ULID)"),
    txn_id: z.string().min(1).max(128),
    actor: z.string().min(1).max(128),
    origin: z.enum(OP_ORIGINS),
    gig_id: z.string().min(1).max(128),
    target: z
      .object({
        collection: z.string().min(1).max(64),
        item_id: z.string().min(1).max(128),
      })
      .strict(),
    kind: z.enum(OP_KINDS),
    fields: z.record(z.string(), z.unknown()),
    hlc: z.string().refine(isHLC, "hlc is not a valid HLC stamp"),
    schema_version: z.number().int().min(1),
    dedup_key: z.string().min(1).max(256).optional(),
  })
  .strict();

// Compile-time proof that the schema and the hand-written spec type agree.
type _OpFromSchema = z.infer<typeof OpSchema>;
const _schemaMatchesOp: Op = null as unknown as _OpFromSchema;
const _opMatchesSchema: _OpFromSchema = null as unknown as Op;
void _schemaMatchesOp;
void _opMatchesSchema;

/** Mint a fresh op_id (ULID: lexicographically sortable, collision-safe, offline-capable — ADR-000). */
export function mintOpId(): string {
  return ulid();
}

/**
 * Validate an unknown payload as an Op. Returns a discriminated result — callers
 * on the server path park failures in the dead-letter queue (CONSTRAINTS #3:
 * never drop input silently).
 */
export function parseOp(
  input: unknown,
): { ok: true; op: Op } | { ok: false; reason: string } {
  const r = OpSchema.safeParse(input);
  if (r.success) return { ok: true, op: r.data };
  const first = r.error.issues[0];
  const where = first && first.path.length > 0 ? `${first.path.join(".")}: ` : "";
  return { ok: false, reason: `invalid_op: ${where}${first?.message ?? "unknown error"}` };
}
